import { motion } from "framer-motion";
import type { LevelCompletePayload } from "../levelTypes";

interface Props {
  payload: LevelCompletePayload;
  levelTitle: string;
  onPlayAgain: () => void;
  onNext: () => void;
}

export function ResultScreen({ payload, levelTitle, onPlayAgain, onNext }: Props) {
  const { stars, goldEarned, wordsLearned } = payload;

  return (
    <div className="result-screen">
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="result-screen__heading">Brett fullført!</h1>
        <p className="result-screen__subtitle">{levelTitle}</p>
      </motion.div>

      {/* Stars */}
      <div className="result-screen__stars" aria-label={`${stars} av 3 stjerner`}>
        {[1, 2, 3].map((n) => {
          const earned = n <= stars;
          return (
            <motion.div
              key={n}
              className={`result-star${earned ? "" : " result-star--empty"}`}
              initial={{ scale: 0, rotate: -20 }}
              animate={earned ? { scale: [0, 1.35, 1], rotate: [0, 8, 0] } : { scale: 1 }}
              transition={{
                delay: earned ? (n - 1) * 0.25 : 0.1,
                type: "spring",
                stiffness: 260,
                damping: 16,
              }}
            >
              {earned ? "⭐" : "☆"}
            </motion.div>
          );
        })}
      </div>

      {/* Stats */}
      <motion.div
        className="result-screen__stats"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <div className="result-stat">
          <span className="result-stat__value">+{goldEarned}</span>
          <span className="result-stat__label">🪙 Gull</span>
        </div>
        <div className="result-stat">
          <span className="result-stat__value">{wordsLearned.length}</span>
          <span className="result-stat__label">Ord lært</span>
        </div>
        <div className="result-stat">
          <span className="result-stat__value">{stars}/3</span>
          <span className="result-stat__label">Stjerner</span>
        </div>
      </motion.div>

      {/* Words learned */}
      {wordsLearned.length > 0 && (
        <motion.div
          className="result-screen__words"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <p className="result-screen__words-label">Ord du lærte</p>
          <div className="result-words-list">
            {wordsLearned.map((w) => (
              <span key={w} className="result-word-chip">{w}</span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Actions */}
      <motion.div
        className="result-screen__actions"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
      >
        <button className="btn-next-level" onClick={onNext}>
          Neste brett →
        </button>
        <button className="btn-play-again" onClick={onPlayAgain}>
          Spill igjen
        </button>
      </motion.div>
    </div>
  );
}
